import { Link } from "react-router-dom";
import { AppShell, NavLink } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleQuestion,
  faRightFromBracket,
  faEnvelope,
  faGear,
  faHouse,
  faSidebar,
} from "@fortawesome/sharp-regular-svg-icons";
import { PropsWithChildren, ReactNode } from "react";

import styles from "./navbar.module.scss";
import { signOutUser } from "@/sign-out";
import { ReferralNavigationButton } from "@/components/referral-navigation/referral-navigation";
import { NAVBAR_WIDTH, NAVBAR_WIDTH_COLLAPSED } from "@/constants/layout";
import { useHelpScout } from "@/hooks/use-help-scout";
import { useBeaconAppealContext } from "@/hooks/use-beacon-appeal-context";

const SIDEBAR_EXPANDED_KEY = "claimable-console-sidebar-expanded";

interface NavbarProps extends PropsWithChildren {
  titleText?: string;
  titleComponent?: ReactNode;
  footer?: ReactNode;
}

export default function Navbar({
  children,
  titleText,
  titleComponent,
  footer,
}: NavbarProps) {
  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>({
    key: SIDEBAR_EXPANDED_KEY,
    defaultValue: true,
    getInitialValueInEffect: false,
  });
  const toggle = () => setIsExpanded((prev) => !prev);
  const { isBeaconReady, toggleBeacon, setSessionData } = useHelpScout();
  useBeaconAppealContext(setSessionData);

  const navLinkClassName = `${styles.navbarNavlink} ${isExpanded ? styles.navbarNavlinkExpanded : styles.navbarNavlinkCollapsed}`;

  const handleLogoutClick = async () => {
    await signOutUser();
  };

  return (
    <div>
      <AppShell
        header={{ height: 90 }}
        navbar={{
          width: isExpanded ? NAVBAR_WIDTH : NAVBAR_WIDTH_COLLAPSED,
          breakpoint: 0,
        }}
      >
        <AppShell.Header className={styles.header}>
          <div className={styles.headerContent}>
            <div
              className={styles.logoAndToggleSection}
              style={{
                width: isExpanded ? NAVBAR_WIDTH : NAVBAR_WIDTH_COLLAPSED,
              }}
            >
              <div className={styles.logoSection}>
                {isExpanded ? (
                  <img
                    className={styles.logoFull}
                    src="/images/logo/logo_black_large.svg"
                    alt="Claimable"
                  />
                ) : (
                  <img
                    className={styles.logoSmall}
                    src="/images/logo/logo_black.svg"
                    alt="Claimable"
                  />
                )}
              </div>
              <button
                className={styles.toggleButton}
                onClick={toggle}
                aria-label="Toggle navigation"
                type="button"
              >
                <FontAwesomeIcon icon={faSidebar} />
              </button>
            </div>
            {titleComponent ? (
              titleComponent
            ) : titleText ? (
              <h1 className={styles.pageTitle}>{titleText}</h1>
            ) : null}
          </div>
          <div className={styles.headerDivider} />
        </AppShell.Header>
        <AppShell.Navbar>
          <div className={styles.navbarLayout}>
            <div className={styles.navbarTop}>
              <div className={styles.buttonContainer}>
                <ReferralNavigationButton
                  className={`${styles.startReferralButton} ${!isExpanded ? styles.startReferralButtonCollapsed : ""}`}
                >
                  {isExpanded ? "Start a referral" : "+"}
                </ReferralNavigationButton>
              </div>
              <div className={styles.navbarNavlinkSection}>
                <NavLink
                  className={navLinkClassName}
                  component={Link}
                  to="/"
                  label={isExpanded ? "Home" : undefined}
                  aria-label="Home"
                  leftSection={<FontAwesomeIcon icon={faHouse} />}
                />
                <NavLink
                  className={navLinkClassName}
                  component={Link}
                  to="/refer"
                  label={isExpanded ? "Refer patient" : undefined}
                  aria-label="Refer patient"
                  leftSection={<FontAwesomeIcon icon={faEnvelope} />}
                />
              </div>
            </div>
            <div className={styles.navbarBottom}>
              <hr />
              <div className={styles.navbarBottomNavlink}>
                <NavLink
                  className={navLinkClassName}
                  component={Link}
                  to="/settings"
                  label={isExpanded ? "Settings" : undefined}
                  aria-label="Settings"
                  leftSection={<FontAwesomeIcon icon={faGear} />}
                />
                <NavLink
                  className={navLinkClassName}
                  component="button"
                  onClick={() => {
                    if (isBeaconReady) {
                      toggleBeacon();
                    } else {
                      window.open("mailto:providers@getclaimable.com");
                    }
                  }}
                  label={isExpanded ? "Help" : undefined}
                  aria-label="Help"
                  leftSection={<FontAwesomeIcon icon={faCircleQuestion} />}
                />
                <NavLink
                  className={navLinkClassName}
                  component="button"
                  onClick={handleLogoutClick}
                  label={isExpanded ? "Logout" : undefined}
                  aria-label="Logout"
                  leftSection={<FontAwesomeIcon icon={faRightFromBracket} />}
                />
              </div>
            </div>
          </div>
        </AppShell.Navbar>
        <AppShell.Main>{children}</AppShell.Main>
        {footer && <AppShell.Footer>{footer}</AppShell.Footer>}
      </AppShell>
    </div>
  );
}
